import { ExtractedField } from '../types';

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  error?: string;
  normalizedValue?: string;
}

// Field validation configuration
export interface FieldValidationConfig {
  required: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  min?: number;
  max?: number;
  customValidator?: (value: string) => ValidationResult;
}

/**
 * Validates text fields
 * Requirements: 4.4 - Validate field formats and display inline error messages
 */
export const validateTextField = (
  value: string,
  config: FieldValidationConfig
): ValidationResult => {
  // Required field validation
  if (config.required && (!value || value.trim().length === 0)) {
    return {
      isValid: false,
      error: 'This field is required',
    };
  }

  // Skip further validation if field is empty and not required
  if (!value || value.trim().length === 0) {
    return { isValid: true, normalizedValue: '' };
  }

  const trimmedValue = value.trim();

  // Length validation
  if (config.minLength && trimmedValue.length < config.minLength) {
    return {
      isValid: false,
      error: `Minimum length is ${config.minLength} characters`,
    };
  }

  if (config.maxLength && trimmedValue.length > config.maxLength) {
    return {
      isValid: false,
      error: `Maximum length is ${config.maxLength} characters`,
    };
  }

  // Pattern validation
  if (config.pattern && !config.pattern.test(trimmedValue)) {
    return {
      isValid: false,
      error: 'Invalid format',
    };
  }

  // Custom validation
  if (config.customValidator) {
    return config.customValidator(trimmedValue);
  }

  return {
    isValid: true,
    normalizedValue: trimmedValue,
  };
};

/**
 * Validates number fields
 * Requirements: 4.4 - Validate field formats for number fields
 */
export const validateNumberField = (
  value: string,
  config: FieldValidationConfig
): ValidationResult => {
  // Required field validation
  if (config.required && (!value || value.trim().length === 0)) {
    return {
      isValid: false,
      error: 'This field is required',
    };
  }

  // Skip further validation if field is empty and not required
  if (!value || value.trim().length === 0) {
    return { isValid: true, normalizedValue: '' };
  }

  const trimmedValue = value.trim();
  const numericValue = parseFloat(trimmedValue);

  // Check if it's a valid number
  if (isNaN(numericValue)) {
    return {
      isValid: false,
      error: 'Must be a valid number',
    };
  }

  // Range validation
  if (config.min !== undefined && numericValue < config.min) {
    return {
      isValid: false,
      error: `Minimum value is ${config.min}`,
    };
  }

  if (config.max !== undefined && numericValue > config.max) {
    return {
      isValid: false,
      error: `Maximum value is ${config.max}`,
    };
  }

  return {
    isValid: true,
    normalizedValue: numericValue.toString(),
  };
};

/**
 * Validates date fields and normalizes to ISO 8601 format
 * Requirements: 4.4 - Validate field formats for date fields
 * Tech Requirements: All dates must be normalized to ISO 8601 (YYYY-MM-DD)
 */
export const validateDateField = (
  value: string,
  config: FieldValidationConfig
): ValidationResult => {
  // Required field validation
  if (config.required && (!value || value.trim().length === 0)) {
    return {
      isValid: false,
      error: 'This field is required',
    };
  }

  // Skip further validation if field is empty and not required
  if (!value || value.trim().length === 0) {
    return { isValid: true, normalizedValue: '' };
  }

  const trimmedValue = value.trim();

  // Try to parse the date
  const date = new Date(trimmedValue);

  // Check if it's a valid date
  if (isNaN(date.getTime())) {
    return {
      isValid: false,
      error: 'Must be a valid date (YYYY-MM-DD format preferred)',
    };
  }

  // Normalize to ISO 8601 format (YYYY-MM-DD)
  const normalizedDate = date.toISOString().split('T')[0];

  return {
    isValid: true,
    normalizedValue: normalizedDate,
  };
};

/**
 * Validates currency fields
 * Requirements: 4.4 - Validate field formats for currency fields
 */
export const validateCurrencyField = (
  value: string,
  config: FieldValidationConfig
): ValidationResult => {
  // Required field validation
  if (config.required && (!value || value.trim().length === 0)) {
    return {
      isValid: false,
      error: 'This field is required',
    };
  }

  // Skip further validation if field is empty and not required
  if (!value || value.trim().length === 0) {
    return { isValid: true, normalizedValue: '' };
  }

  let trimmedValue = value.trim();

  // Remove common currency symbols and formatting
  const cleanedValue = trimmedValue
    .replace(/[$,\s]/g, '') // Remove $, commas, and spaces
    .replace(/^-/, ''); // Remove leading negative sign for now

  // Check if the cleaned value is a valid number
  const numericValue = parseFloat(cleanedValue);

  if (isNaN(numericValue)) {
    return {
      isValid: false,
      error: 'Must be a valid currency amount',
    };
  }

  // Range validation (currency should be positive in most cases)
  if (config.min !== undefined && numericValue < config.min) {
    return {
      isValid: false,
      error: `Minimum amount is $${config.min.toFixed(2)}`,
    };
  }

  if (config.max !== undefined && numericValue > config.max) {
    return {
      isValid: false,
      error: `Maximum amount is $${config.max.toFixed(2)}`,
    };
  }

  // Normalize to standard currency format (2 decimal places)
  const normalizedValue = numericValue.toFixed(2);

  return {
    isValid: true,
    normalizedValue: normalizedValue,
  };
};

/**
 * Main validation function that routes to appropriate validator based on field type
 * Requirements: 4.4 - Validate field formats for all field types
 */
export const validateField = (
  field: ExtractedField,
  value: string,
  customConfig?: Partial<FieldValidationConfig>
): ValidationResult => {
  // Build validation configuration
  const config: FieldValidationConfig = {
    required: field.required,
    ...customConfig,
  };

  // Apply validation rules from field definition
  if (field.validationRules) {
    for (const rule of field.validationRules) {
      switch (rule.type) {
        case 'required':
          config.required = true;
          break;
        case 'pattern':
          if (typeof rule.value === 'string') {
            config.pattern = new RegExp(rule.value);
          }
          break;
        case 'range':
          if (typeof rule.value === 'number') {
            config.min = rule.value;
          }
          break;
      }
    }
  }

  // Apply the analyzer field-schema validation rule (Sprint M2): pattern/min/max + message.
  if (field.validationRule) {
    const vr = field.validationRule;
    if (vr.pattern) {
      try { config.pattern = new RegExp(vr.pattern); } catch { /* ignore invalid pattern */ }
    }
    if (typeof vr.min === 'number') config.min = vr.min;
    if (typeof vr.max === 'number') config.max = vr.max;
  }

  // Route to appropriate validator based on field type
  let result: ValidationResult;
  switch (field.fieldType) {
    case 'number':
      result = validateNumberField(value, config);
      break;
    case 'date':
      result = validateDateField(value, config);
      break;
    case 'currency':
      result = validateCurrencyField(value, config);
      break;
    case 'text':
    default:
      result = validateTextField(value, config);
      break;
  }

  // Prefer the schema-provided message when the field is invalid (Sprint M2).
  if (!result.isValid && field.validationRule?.message) {
    return { ...result, error: field.validationRule.message };
  }
  return result;
};

/**
 * Validates all fields in a form
 */
export const validateAllFields = (
  fields: ExtractedField[],
  values: Record<string, string>
): Record<string, ValidationResult> => {
  const results: Record<string, ValidationResult> = {};

  for (const field of fields) {
    const value = values[field.id] || '';
    results[field.id] = validateField(field, value);
  }

  return results;
};

/**
 * Checks if all validations are passing
 */
export const areAllFieldsValid = (validationResults: Record<string, ValidationResult>): boolean => {
  return Object.values(validationResults).every(result => result.isValid);
};